import { NgStyle } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    Injector,
    inject,
    input,
    type OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ConfirmationService,
    type MenuItem,
    type MenuItemCommandEvent,
} from '@openng/optimus-ui/api';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MenuModule } from '@openng/optimus-ui/menu';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { ArrayStore } from 'devextreme/common/data';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { injectParams } from 'ngxtension/inject-params';
import { finalize, map, skipWhile } from 'rxjs';

import { DiffViewerComponent } from '@app/shared/components/diff-viewer/diff-viewer.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { COPYABLE_OBJECTS } from '@app/shared/components/registry/action-btns/action-btns.component';
import { RegistryService } from '@app/shared/components/registry/registry.service';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { GGX_GLOBAL_OBJECTS, OBJECTS, SKELETON_TYPES } from '@app/shared/constants/shared.constant';
import { CorDxGridSearchDirective } from '@app/shared/directives/dx-grid-search.directive';
import type { AttachmentFile } from '@app/shared/interface/common.interface';
import type { DxDataGridColumn } from '@app/shared/interface/devextreme.interface';
import type { NgActivatedRoute } from '@app/shared/interface/ng-router.interface';
import { LocalDatePipe } from '@app/shared/pipes/date-format.pipe';
import { NameBadgePipe } from '@app/shared/pipes/name-badge.pipe';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import type { WithLogService } from '@app/shared/services/mixins/log.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { FormHelper } from '@app/shared/utils/form-helper';
import { Helpers, parseAsNumber } from '@app/shared/utils/helpers';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/utils/object-service-mapper';
import { RouterHelper } from '@app/shared/utils/router-helper';

export interface ChangeLogAdapter {
    action: string;
    actor: string;
    changeMessage: string;
    comment: string;
    createdBy: string;
    createdDate: string;
    id: number;
    versionName: string;
    transactionId: number;
    latestLogId: number;
    menuItems?: MenuItem[];
}

@Component({
    selector: 'cor-change-log',
    templateUrl: './change-log.component.html',
    styleUrls: ['./change-log.component.scss'],
    imports: [
        ReactiveFormsModule,
        LocalDatePipe,
        TooltipModule,
        DxDataGridModule,
        CorDxGridSearchDirective,
        InputContainerComponent,
        InputTextModule,
        DialogModule,
        SkeletonComponent,
        MenuModule,
        UniqueRgbColorPipe,
        NameBadgePipe,
        SelectButtonModule,
        FormsModule,
        ButtonModule,
        DiffViewerComponent,
        NgStyle,
        CardModule,
        AvatarModule,
    ],
})
export class ChangeLogComponent implements OnInit {
    readonly dataGrid = viewChild(DxDataGridComponent);
    readonly id = input<number, unknown>(undefined, { transform: parseAsNumber });
    readonly currentProject = injectParams<string>('projectSlug');
    protected readonly SKELETON_TYPES = SKELETON_TYPES;
    protected readonly OBJECTS = OBJECTS;

    protected readonly object = signal<OBJECTS>(null);
    protected readonly logsLoading = signal(true);
    protected readonly isSaving = signal(false);
    protected readonly isVisible = signal(false);
    protected readonly logsFilter = signal<'all' | 'named'>('all');

    private service: WithLogService;
    private readonly allLogs = signal<ChangeLogAdapter[]>([]);
    protected readonly columns: DxDataGridColumn[] = [
        {
            caption: 'Created Date',
            dataField: 'createdDate',
            //FIXME: Ideally, we should use dataType="date" which formats the header-filter correctly.
            //But unable to get that working with the right moment commands. So, using dataType=string for now.
            dataType: 'string',
            sortOrder: 'desc',
            cellTemplate: 'dateTemplate',
            width: 'auto',
        },
        {
            caption: 'Author',
            dataField: 'actor',
            cellTemplate: 'avatarTemplate',
            cssClass: 'tw:max-w-[20rem]',
            width: 'auto',
        },
        {
            caption: 'Snapshot Name',
            dataField: 'versionName',
            allowSorting: false,
            allowEditing: true,
            cssClass: 'tw:max-w-[20rem]',
            width: 'auto',
        },
        {
            caption: 'Action',
            dataField: 'action',
            width: 'auto',
        },
        {
            caption: 'Message',
            dataField: 'changeMessage',
            allowSorting: false,
            cellTemplate: 'changeMessageTemplate',
        },
        {
            visible: false,
            caption: '',
            dataField: 'comment',
        },
        {
            caption: 'Actions',
            cellTemplate: 'actionTemplate',
            width: 100,
        },
    ];
    protected readonly store = computed(
        () =>
            new ArrayStore({
                data:
                    this.logsFilter() === 'named'
                        ? this.allLogs().filter(log => !!log.versionName)
                        : this.allLogs(),
            }),
    );
    protected changeLogOptions: MenuItem[] = [];
    protected latestChangeLogOptions: MenuItem[] = [];

    protected saveLogVersionNameForm: FormGroup<{
        id: FormControl<number>;
        versionName: FormControl<string>;
    }>;

    private readonly route = inject(ActivatedRoute) as NgActivatedRoute<
        { object: OBJECTS },
        { id: number }
    >;

    protected snapshotChanges = {
        showButton: computed(
            () => GGX_GLOBAL_OBJECTS.includes(this.object()) && this.object() !== OBJECTS.REPORT,
        ),
        isVisible: signal(false),
        isLoading: signal(true),
        oldSnapshot: signal<{
            definition: string;
            promptTemplate?: string;
            knowledgeFile?: AttachmentFile;
        }>(null),
        newSnapshot: signal<{
            definition: string;
            promptTemplate?: string;
            knowledgeFile?: AttachmentFile;
        }>(null),
        onShow: (data: ChangeLogAdapter) => {
            this.snapshotChanges.isLoading.set(true);
            this.snapshotChanges.isVisible.set(true);
            this.service
                .getChangeLogDiff(this.id(), data.transactionId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(({ result }) => {
                    this.snapshotChanges.oldSnapshot.set(result.previous);
                    this.snapshotChanges.newSnapshot.set(result.current);
                })
                .add(() => this.snapshotChanges.isLoading.set(false));
        },
        onHide: () => {
            this.snapshotChanges.isVisible.set(false);
            this.snapshotChanges.oldSnapshot.set(null);
            this.snapshotChanges.newSnapshot.set(null);
        },
    };

    protected readonly router = inject(Router);
    private readonly injector = inject(Injector);
    private readonly toast = inject(ToastNotificationService);
    protected readonly registryService = inject(RegistryService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly confirmationService = inject(ConfirmationService);

    protected readonly objectLink = computed(() =>
        RouterHelper.buildRoute({
            object: this.object(),
            id: this.id(),
            projectSlug: this.currentProject(),
        }),
    );

    ngOnInit() {
        this.registryService.itemActions$
            .pipe(
                skipWhile(itemAction => !itemAction), // Skip if itemAction is null, undefined, or falsy
                // You can add more conditions here if needed
                // e.g., skipWhile(itemAction => itemAction === null || itemAction === undefined
                // || itemAction.someProperty !== 'someValue')
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(itemAction => {
                // Preview is shown to user having Read/Write access
                this.changeLogOptions = [
                    {
                        id: 'previewSnapshot',
                        label: 'Preview Snapshot',
                        icon: 'pi pi-eye',
                    },
                ];

                // User with write access can only edit log name and restore the snapshot
                if (itemAction?.Write) {
                    // show only edit name option for the latest log, since the current object is same
                    // as its snapshot
                    this.latestChangeLogOptions = [
                        {
                            id: 'editName',
                            label: 'Edit Snapshot Name',
                            icon: 'pi pi-pencil',
                        },
                    ];

                    this.changeLogOptions.push({
                        id: 'editName',
                        label: 'Edit Snapshot Name',
                        icon: 'pi pi-pencil',
                    });

                    this.changeLogOptions.push({
                        id: 'restoreSnapshot',
                        label: 'Restore Snapshot',
                        icon: 'pi pi-history',
                    });
                }

                if (COPYABLE_OBJECTS.includes(this.object())) {
                    this.changeLogOptions.push({
                        id: 'restoreSnapshotAsCopy',
                        label: 'Restore Snapshot as Copy',
                        icon: 'pi pi-history',
                    });
                }
            });

        this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
            this.object.set(data.object);
            this.service = this.injector.get<any>(OBJECT_SERVICE_MAPPER[this.object()]);
            this.getLogs();
        });
    }

    protected onLogPreviewOptionChange(id: string, data: ChangeLogAdapter) {
        if (id === 'editName') {
            // Open a Pop up if Event is to Edit Log Name
            this.isVisible.set(true);
            // Create a formGroup for Version Name Update
            this.saveLogVersionNameForm = new FormGroup({
                id: new FormControl(data.id, { validators: [Validators.required] }),
                versionName: new FormControl(data.versionName, {
                    validators: [Validators.required],
                }),
            });
        } else if (id === 'previewSnapshot') {
            // Re-route to preview endpoint
            this.previewSnapshot(data.transactionId);
        } else if (id === 'restoreSnapshot') {
            // Restore Re-route to restored object
            this.restoreSnapshot(data);
        } else if (id === 'restoreSnapshotAsCopy') {
            // Restore Re-route to copied restored object
            this.restoreSnapshotAsCopy(data.transactionId, data.versionName);
        }
    }

    protected onVersionNameSubmit() {
        if (!this.saveLogVersionNameForm.valid) {
            FormHelper.showFormErrors(this.saveLogVersionNameForm, this.toast);

            return;
        }
        const { id, versionName } = this.saveLogVersionNameForm.value;
        this.isSaving.set(true);
        this.service
            .patchVersionName(this.id(), { id, versionName })
            .subscribe(() => {
                this.getLogs();
                this.toast.showSuccess(`${'Version Name is updated successfully'}`);
                this.isVisible.set(false); // close popup on successful update
            })
            .add(() => {
                this.isSaving.set(false);
            });
    }

    private previewSnapshot(transactionId: number) {
        if (transactionId === undefined) {
            this.toast.showError("Can't preview this snapshot as it is not tracked.");

            return;
        }
        this.router.navigate([this.objectLink(), 'preview', transactionId]);
    }

    private restoreSnapshot(data: ChangeLogAdapter) {
        // Restore Re-route to restored object
        if (!data.transactionId) {
            this.toast.showError("Can't restore this snapshot as it is not tracked.");
            return;
        }

        this.confirmationService.confirm({
            key: 'confirm-dialog',
            message: `Restoring '${data.versionName}' will update existing data, Are you sure you want to restore ?`,
            closeOnEscape: true,
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            accept: () => {
                this.service.restoreSnapshot(this.id(), data.transactionId).subscribe({
                    next: () => {
                        this.router.navigate([this.objectLink()]);
                        this.toast.showSuccess(`${`Successfully restored "${data.versionName}"`}`);
                    },
                    error: () => {
                        this.toast.showError(`${`Failed to restored "${data.versionName}"`}`);
                    },
                });
            },
        });
    }

    private restoreSnapshotAsCopy(transactionId: number, versionName: string) {
        if (transactionId === undefined) {
            this.toast.showError("Can't restore this snapshot as it is not tracked.");
            return;
        }

        this.service.restoreSnapshotAsCopy(this.id(), transactionId).subscribe({
            next: res => {
                this.toast.showSuccess(`${`Successfully Copied Snapshot "${versionName}"`}`);

                this.router.navigate([
                    `${RouterHelper.buildRoute({
                        object: this.object(), // always required
                        id: res.result.id, // always required
                        projectSlug: this.currentProject(),
                    })}`,
                ]);
            },
            error: () => {
                this.toast.showError(`${`Failed to restored "${versionName}"`}`);
            },
        });
    }

    private getLogs() {
        this.logsLoading.set(true);
        this.service
            .getChangeLog(this.id())
            .pipe(
                map(response => {
                    this.allLogs.set(
                        response.result.map(res => {
                            const rowData: ChangeLogAdapter = {
                                ...res,
                                versionName:
                                    res.versionName ||
                                    Helpers.getPlaceholderName('Snapshot at', res.createdDate),
                            };
                            // Attach menu items to each row
                            const options =
                                rowData.latestLogId === rowData.id
                                    ? this.latestChangeLogOptions
                                    : this.changeLogOptions;
                            rowData.menuItems = options.map(option => ({
                                ...option,
                                data: option.id,
                                command: (event: MenuItemCommandEvent) => {
                                    const id = event.item.data as string;
                                    this.onLogPreviewOptionChange(id, rowData);
                                },
                            }));
                            return rowData;
                        }),
                    );
                }),
                finalize(() => this.logsLoading.set(false)),
            )
            .subscribe();
    }
}
